import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, ShoppingCart } from 'lucide-react';
import { Course } from '../../types/course';
import { BASE_URL } from '../../apiConfig';
import { useAuth } from '../../contexts/AuthContext';

interface SuggestedCourseCardProps {
  course: Course;
  compact?: boolean;
}

const getCourseImageUrl = (course: Course): string => {
  if (!course.imagePath) return '/default-course.jpg';
  if (course.imagePath.startsWith('http')) return course.imagePath;
  const path = course.imagePath.startsWith('/') ? course.imagePath.slice(1) : course.imagePath;
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
  return `${baseUrl}${path}`;
};

const SuggestedCourseCard: React.FC<SuggestedCourseCardProps> = ({ course, compact = false }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const price = Number(course.price) || 0;
  const isFree = !(price > 0);
  const formattedPrice = price > 0 ? price.toLocaleString('ar-EG') : '0';
  const detailPath = `/courses/${course.id}`;

  const handleSubscribe = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login', { state: { redirectTo: detailPath } });
      return;
    }
    navigate(detailPath);
  };

  return (
    <article className="group relative h-full w-full">
      <div className="relative flex h-full flex-col overflow-visible rounded-2xl border border-brand-100 bg-white shadow-sm transition-all duration-300 group-hover:border-brand-300 group-hover:shadow-lg sm:pb-32">
        <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden rounded-t-2xl bg-brand-50 sm:aspect-[16/9]">
          <img
            src={getCourseImageUrl(course)}
            alt={course.courseName}
            className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = '/default-course.jpg';
            }}
          />
          {isFree && (
            <span
              className={`absolute right-2 top-2 z-10 rounded-full bg-brand-600 font-bold text-white ${
                compact ? 'px-2 py-0.5 text-[10px]' : 'px-3 py-1 text-xs'
              }`}
            >
              مجاني
            </span>
          )}
        </div>

        <div
          className={`relative z-20 -mt-5 ml-1 mr-6 mb-3 flex flex-1 flex-col rounded-xl border border-brand-100 bg-white shadow-md transition-all duration-300 group-hover:border-brand-300 group-hover:shadow-lg sm:absolute sm:bottom-2 sm:left-1 sm:right-6 sm:mx-0 sm:mb-0 sm:-mt-0 ${
            compact ? 'p-3.5 sm:p-4' : 'p-4 sm:p-5'
          }`}
        >
          <h3
            className={`mb-1.5 line-clamp-2 font-bold text-slate-900 ${
              compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'
            }`}
          >
            {course.courseName}
          </h3>
          <p
            className={`mb-2 line-clamp-2 leading-relaxed text-slate-600 sm:mb-3 ${
              compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'
            }`}
          >
            {course.description || 'كورس تعليمي مميز لطلاب الثانوية'}
          </p>
          <div className="mb-2 h-0.5 w-12 bg-brand-500 sm:mb-3" />

          <div className={`mb-2.5 ${compact ? '' : 'sm:mb-3'}`}>
            {isFree ? (
              <span className={`font-bold text-brand-600 ${compact ? 'text-sm' : 'text-base'}`}>مجاناً</span>
            ) : (
              <div className="flex items-baseline gap-1">
                <span className={`font-bold text-slate-900 ${compact ? 'text-sm sm:text-base' : 'text-base'}`}>
                  {formattedPrice}
                </span>
                <span className={`font-semibold text-brand-600 ${compact ? 'text-xs sm:text-sm' : 'text-sm'}`}>
                  جنيه
                </span>
              </div>
            )}
          </div>

          <div className={`mt-auto grid grid-cols-2 ${compact ? 'gap-2' : 'gap-2.5'}`}>
            <Link
              to={detailPath}
              onClick={(e) => e.stopPropagation()}
              className={`relative z-30 inline-flex min-h-10 items-center justify-center rounded-lg border border-brand-600 font-semibold text-brand-600 transition hover:bg-brand-50 ${
                compact ? 'gap-1 px-2 py-2 text-[11px] sm:text-xs' : 'gap-1.5 px-3 py-2.5 text-xs sm:text-sm'
              }`}
            >
              <BookOpen className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              التفاصيل
            </Link>
            <button
              type="button"
              onClick={handleSubscribe}
              className={`relative z-30 inline-flex min-h-10 items-center justify-center rounded-lg bg-brand-600 font-semibold text-white transition hover:bg-brand-700 ${
                compact ? 'gap-1 px-2 py-2 text-[11px] sm:text-xs' : 'gap-1.5 px-3 py-2.5 text-xs sm:text-sm'
              }`}
            >
              <ShoppingCart className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
              {isFree ? 'مجاناً' : 'اشترك'}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default SuggestedCourseCard;
